// ── ADDITIONAL (MANUAL) PROPOSAL DISCOUNT ───────────────────────────────
//
// A rep-negotiated reduction on the whole proposal — "take $4,000 off", or
// "give them 5%". Stored on `inputs.proposalDiscount` as { kind, value }.
//
// It is its OWN line, never folded into another figure. vizNet, the bundle,
// every line price stay exactly what the rate card (and any override) made
// them; the discount is printed as "Additional discount −$X" and comes off:
//   • oneTimeAllIn / grandTotal on the calc (so every total reads it), and
//   • the visualization split the payment plan divides (vizSubtotal), so the
//     milestones sum to the discounted one-time total.
// The backend mirror is `proposal_discount_of()` in
// backend/services/vexos_payment_plan.py, which subtracts the saved
// `calc_snapshot.proposalDiscount` inside plan_from_quote().
//
// The base a % applies to is the one-time scope EXCLUDING on-site travel
// (a flat pass-through, as with pay-in-full): setup fee + net visualization +
// revisions. Either kind is capped at the visualization split (net viz +
// revisions) because that is the pot it is taken out of — a discount bigger
// than the visualization work would make a milestone negative.
//
// Recurring money (the monthly platform subscription, marketing management)
// is never touched, nor are marketing setup / collateral, which bill outside
// the visualization split.

export const DISCOUNT_AMOUNT = "amount";
export const DISCOUNT_PCT = "pct";

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** The persisted value, or null when there is no discount. Anything that is
 *  not a positive number of a known kind reads as "no discount". */
export function normalizeProposalDiscount(raw) {
  if (!raw || typeof raw !== "object") return null;
  const kind = raw.kind === DISCOUNT_PCT ? DISCOUNT_PCT : (raw.kind === DISCOUNT_AMOUNT ? DISCOUNT_AMOUNT : null);
  const value = num(raw.value);
  if (!kind || value <= 0) return null;
  return { kind, value: kind === DISCOUNT_PCT ? Math.min(value, 100) : Math.round(value) };
}

/** The $ the discount is worth on a quote with these figures. */
export function proposalDiscountAmount(discount, { setupFee = 0, vizNet = 0, revisionsCost = 0 }) {
  const d = normalizeProposalDiscount(discount);
  if (!d) return 0;
  const split = Math.max(0, num(vizNet) + num(revisionsCost));
  const base = split + Math.max(0, num(setupFee));
  const raw = d.kind === DISCOUNT_PCT ? Math.round(base * (d.value / 100)) : d.value;
  return Math.max(0, Math.min(raw, split));
}

/** The calc with the discount stamped on it and taken off its totals.
 *  `isAlacarte` leaves the setup fee out of a % base — à la carte bills no
 *  platform, and the setup fee on the calc is zeroed after this runs. */
export function applyProposalDiscount(calc, discount, { isAlacarte = false } = {}) {
  const d = normalizeProposalDiscount(discount);
  const amount = d
    ? proposalDiscountAmount(d, {
        setupFee: isAlacarte ? 0 : calc.setupFee,
        vizNet: calc.vizNet,
        revisionsCost: calc.revisionsCost,
      })
    : 0;
  if (amount <= 0) {
    return { ...calc, proposalDiscount: 0, proposalDiscountPct: null };
  }
  return {
    ...calc,
    proposalDiscount: amount,
    proposalDiscountPct: d.kind === DISCOUNT_PCT ? d.value : null,
    oneTimeAllIn: num(calc.oneTimeAllIn) - amount,
    grandTotal: num(calc.grandTotal) - amount,
  };
}

/** Client-facing label: "Additional discount" or "Additional discount (5%)". */
export function proposalDiscountLabel(pct) {
  return num(pct) > 0 ? `Additional discount (${fmtPct(pct)}%)` : "Additional discount";
}

function fmtPct(p) {
  const n = num(p);
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}
